import { ErrorBoundary } from "@sentry/react";
import React, { useContext, useEffect } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { setPageName } from "../../actions";
import { ErrorMsg } from "../../components/AppMessages";
import { DashboardLayout } from "../../components/Layouts";
import { DefaultPageContent } from "../../components/PageContent";
import SeoPageTitle from "../../components/PageContent/subs/SeoPageTitle";
import { eventTracking, MixpanelEvents } from "../../utils/userTracking";
import { useRequestAccessByPath } from "../../hooks/react-query/access-requests/useRequestAccessByPath";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import { useQuery } from "@tanstack/react-query";
import {
  getGeoReportingLastRefreshDate,
  getGeoReportingOverviewSummary,
  getMarketOverview,
  getGeoReportingStrategicThemesOverview,
  getGeoReportingNissanVsCompetitorsOverview,
  getEvolutionOverTime,
  getEvolutionOverTimeByDomain,
  getOverviewOfSourceDomains,
  getOverviewOfPromptPerformance,
  downloadMarketOverviewCsv,
  downloadStrategicThemesCsv,
  downloadNissanVsCompetitorsOverviewCsv,
  downloadEvolutionOverTimeCsv,
  downloadSourceDomainsOverviewCsv,
  downloadEvolutionOverTimeByDomainCsv,
  downloadPromptPerformanceCsv,
} from "../../api/geoReportingApi";
import { toast } from "react-toastify";
import { isExportDownload, triggerExportDownload } from "../../utils/downloadExport";
import SummaryTile from "./components/SummaryTile";
import StrategicThemesTable from "./components/StrategicThemesTable";
import EvolutionOverTime from "./components/EvolutionOverTime";
import { ThemeContext } from "../../context";

import OverviewOfPromptPerformanceTable from "./components/OverviewOfPromptPerformanceTable";
import { GeoReportingFilters } from "../../components/Filters/GeoReportingFilters";
import Collapsible from "../../components/Collapsible/Collapsible";
import MarketOverviewTable from "./components/MarketOverviewTable";
import PageTitle from "../../components/PageContent/subs/PageTitle";
import OverviewOfSourceDomainsTable from "./components/OverviewOfSourceDomainsTable";
import DomainFilters from "./components/DomainFilters";
import { RestrictedAccess } from "../../components/RestrictedAccess/RestrictedAccess";
import "../../assets/styles/pages/geoReporting.scss";

const CsvDownloadButton = ({ onDownload, title }: { onDownload: () => Promise<any>; title: string }) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toast.success(<div>CSV downloading</div>, { autoClose: 10000, pauseOnHover: true });
    onDownload()
      .then((res: any) => {
        if (isExportDownload(res)) {
          triggerExportDownload(res);
        } else {
          toast.error("An error occurred while downloading CSV file");
        }
      })
      .catch(() => toast.error("An error occurred while downloading CSV file"));
  };

  return (
    // `title` used to only feed the client-side filename (saveAs(..., `geo reporting - ${title}.csv`))
    // - the backend's own ExportDownloadOut.file_name is the real source of truth for that now
    // (see triggerExportDownload). Kept as a real HTML tooltip instead of becoming dead weight.
    <button className="geo-csv-download-btn" onClick={handleClick} title={`Download ${title} CSV`}>
      <FileDownloadIcon fontSize="small" /> CSV
    </button>
  );
};

export const GEOReporting = withRouter((props: RouteComponentProps): JSX.Element => {
  const dispatch = useDispatch();
  const { location, history } = props;
  const { theme } = useContext(ThemeContext);

  const canAccessView = useSelector((state: RootStateOrAny) => state.page.can_access_view);

  // Stays on its old bespoke endpoints (geoReportingApi.ts's own download*Csv functions) - the
  // live _dashboard.key for this page's route (/seo_dashboard/geo_reporting) is
  // "poi_seo_dashboard_geo_reporting" (confirmed against a real GET /dashboards response -
  // dashboardRegistry.ts's own comment), but ANALYSIS_REGISTRY (backend) is keyed by the literal
  // string "geo_reporting" with no fallback/fuzzy match (services/analysis/service.py's own
  // dashboard.key lookup) - so exporting through the generic /analysis/{id}/export endpoint 404s
  // regardless of which real dashboard_id gets resolved.

  const { requestAccessByPath } = useRequestAccessByPath();

  const handleRequestAccess = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      requestAccessByPath(location.pathname, {
        onSuccess: () => {
          eventTracking(MixpanelEvents.dashboard_access_request, { dashboard: "GEO Reporting" });
          toast.success("Request for access has been received");
        },
        onError: () => toast.error("Failed to update your request, please try again later"),
      });
    },
    [location.pathname, requestAccessByPath]
  );

  useEffect(() => {
    Promise.all([dispatch(setPageName("GEO reporting"))]);
  }, [dispatch]);

  const searchWithoutDomain = React.useMemo(() => {
    const p = new URLSearchParams(history.location.search);
    p.delete("domain");
    p.delete("funnel");
    return p.toString();
  }, [history.location.search]);

  const searchForSummary = React.useMemo(() => {
    const p = new URLSearchParams(history.location.search);
    p.delete("domain");
    p.delete("funnel");
    p.delete("strategic_theme");
    return p.toString();
  }, [history.location.search]);

  const guardedRefetch = React.useCallback(
    (fn: () => void) => {
      const urlDateRange = new URLSearchParams(history.location.search).get("date_range");
      if (urlDateRange) fn();
    },
    [history.location.search]
  );

  const { data, refetch } = useQuery({
    queryKey: ["geoReportingRefreshDate"],
    queryFn: () => getGeoReportingLastRefreshDate(),
    enabled: false,
  });

  React.useEffect(() => {
    guardedRefetch(refetch);
  }, [searchWithoutDomain]);

  const { data: summaryData, refetch: refetchSummary } = useQuery({
    queryKey: ["geoReportingOverviewSummary"],
    queryFn: () => getGeoReportingOverviewSummary(),
    enabled: false,
  });

  React.useEffect(() => {
    guardedRefetch(refetchSummary);
  }, [searchForSummary]);

  const { data: competitorsData } = useQuery({
    queryKey: ["geoReportingNissanVsCompetitors", searchWithoutDomain],
    queryFn: () => getGeoReportingNissanVsCompetitorsOverview(),
    enabled: false,
  });

  const top10CompetitorTags = React.useMemo(() => {
    if (!competitorsData?.data?.length) return undefined;
    return [...competitorsData.data]
      .sort((a: any, b: any) => (b.sov ?? 0) - (a.sov ?? 0))
      .slice(0, 10)
      .map((row: any) => row.tag as string);
  }, [competitorsData]);

  return (
    <DashboardLayout>
      <DefaultPageContent
        filter={
          <GeoReportingFilters
            lastDataRefresh={data?.lastDataRefresh ?? ""}
            filterBtns={[
              { id: "seo_dashboard_btn", name: "SEO Dashboard", navigateTo: "/seo_dashboard" },
              { id: "search_interest_btn", name: "Search Interest", navigateTo: "/seo_dashboard/search_interest_report" },
            ]}
          />
        }
        lastDataRefresh={data ?? ""}
        showLastDataRefreshAlert={true}
        dataDocumentation="geo_reporting"
        pageTitle={<PageTitle dataDocumentation="geo_reporting" />}
      >
        {!canAccessView ? (
          <RestrictedAccess onRequestAccess={handleRequestAccess} />
        ) : (
          <ErrorBoundary fallback={<ErrorMsg />}>
            <div className="geo_reporting" style={!canAccessView ? { display: "none" } : undefined}>
              <Collapsible
                title="Markets Overview"
                show={true}
                toggle={<CsvDownloadButton onDownload={() => downloadMarketOverviewCsv()} title="Markets Overview" />}
              >
                <div className="market-overview-container gdr_tile primary_tile_background">
                  <MarketOverviewTable queryFn={getMarketOverview} summaryData={summaryData} />
                </div>
              </Collapsible>

              <div className="geo-reporting-tab">
                <div className="summary-tiles">
                  <SummaryTile title="SoV" value={summaryData?.sov} comparison={summaryData?.sov_change} isPercentageValue={true} />
                  <SummaryTile
                    title="Average Sentiment"
                    value={summaryData?.average_sentiment}
                    comparison={summaryData?.average_sentiment_change}
                    isPercentageValue={true}
                  />
                </div>
              </div>

              <Collapsible
                title="Overview of Strategic Themes"
                show={true}
                toggle={<CsvDownloadButton onDownload={() => downloadStrategicThemesCsv()} title="Overview of Strategic Themes" />}
              >
                <StrategicThemesTable
                  title=""
                  queryKey="geoReportingStrategicThemes"
                  queryFn={getGeoReportingStrategicThemesOverview}
                  summaryData={summaryData}
                />
              </Collapsible>

              <Collapsible
                title="Nissan's Overall Performance vs Competitors"
                show={true}
                toggle={
                  <CsvDownloadButton
                    onDownload={() => downloadNissanVsCompetitorsOverviewCsv()}
                    title="Nissan's Overall Performance vs Competitors"
                  />
                }
              >
                <StrategicThemesTable
                  title=""
                  queryKey="geoReportingNissanVsCompetitors"
                  queryFn={getGeoReportingNissanVsCompetitorsOverview}
                  summaryData={summaryData}
                />
                <div className="geo-section-sub-header">
                  <CsvDownloadButton onDownload={() => downloadEvolutionOverTimeCsv()} title="Evolution Over Time" />
                </div>
                <EvolutionOverTime
                  theme={theme}
                  queryFn={getEvolutionOverTime}
                  queryKey="geoReportingEvolutionOverTime"
                  ignoreDomainParam={true}
                  limitToSeries={top10CompetitorTags}
                  maxSeries={10}
                  showPercentage={true}
                />
              </Collapsible>

              <Collapsible
                title="Overview of Source Domains"
                show={true}
                toggle={<CsvDownloadButton onDownload={() => downloadSourceDomainsOverviewCsv()} title="Overview of Source Domains" />}
              >
                <div className="gdr_tile primary_tile_background">
                  <DomainFilters />
                  <OverviewOfSourceDomainsTable
                    title=""
                    queryKey="geoReportingOverviewOfSourceDomains"
                    queryFn={getOverviewOfSourceDomains}
                  />
                  <div className="geo-section-sub-header">
                    <CsvDownloadButton onDownload={() => downloadEvolutionOverTimeByDomainCsv()} title="Evolution Over Time by Domain" />
                  </div>
                  <EvolutionOverTime
                    theme={theme}
                    queryFn={getEvolutionOverTimeByDomain}
                    queryKey="geoReportingEvolutionOverTimeByDomain"
                    maxSeries={10}
                  />
                </div>
              </Collapsible>

              <Collapsible
                title="Overview of Prompt Performance"
                show={true}
                toggle={<CsvDownloadButton onDownload={() => downloadPromptPerformanceCsv()} title="Overview of Prompt Performance" />}
              >
                <OverviewOfPromptPerformanceTable
                  title=""
                  queryKey="geoReportingOverviewOfPromptPeerformance"
                  queryFn={getOverviewOfPromptPerformance}
                  summaryData={summaryData}
                />
              </Collapsible>
            </div>
          </ErrorBoundary>
        )}
      </DefaultPageContent>
    </DashboardLayout>
  );
});
